import { formatDate } from "@progress/kendo-intl";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { LoadingIndicator } from "../../../core/loading-indicator.component";
import { Grid, GridColumn, GridNoRecords } from "@progress/kendo-react-grid";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import axios from "axios";
import { getDataWithToken, getEndPoint } from "../../../Utils/network.client";
import { API_END_POINT } from "../../../constants";
import RouteAbstractModel from "../../../models/reports/routeabstract-reports";
import { PDFExport } from "@progress/kendo-react-pdf";
import { useGetCompanyDetails } from "../../../apiclient/master/useGetCompanydetailsApi";

interface IRouteAbstractReport {}

export const RouteAbstractReport: React.FC = () => {
  const [reportMenu, setReportMenu] = useState<IRouteAbstractReport[]>([]);
  const [fromDate, setFromDate] = useState<Date>(
    new Date(dayjs().add(0, "months").format("MM/DD/YYYY"))
  );
  const [RouteAbstractList, setRouteAbstractList] = useState<
    RouteAbstractModel[]
  >([]);
  const [toDate, setToDate] = useState<Date>(new Date());
  const exportExcel = useRef<ExcelExport | null>(null);
  const pdfExportRef = useRef<PDFExport | null>(null);
  const excelExportRef = useRef<ExcelExport | null>(null);
  const { data: companyDetails, isLoading: isCompanyLoading } = useGetCompanyDetails();
  const companyName = companyDetails?.[0]?.companyName || "Company Name";

  const exportToExcel = () => {
    if (!excelExportRef.current) return;

    const options = excelExportRef.current.workbookOptions();
    const sheet = options.sheets[0];

    const formattedFromDate = fromDate
      ? new Date(fromDate).toLocaleDateString("en-IN", {
          day: "numeric",
          month: "numeric",
          year: "numeric",
        })
      : "";

    const formattedToDate = toDate
      ? new Date(toDate).toLocaleDateString("en-IN", {
          day: "numeric",
          month: "numeric",
          year: "numeric",
        })
      : "";

    const headingWithDate = `Route Abstract Report - ${formattedFromDate} to ${formattedToDate}`;

    sheet.rows.unshift({
      cells: [
        {
          value: headingWithDate,
          colSpan: sheet.columns.length,
          bold: true,
          fontSize: 14,
          textAlign: "center",
        },
      ],
    });
    sheet.rows.unshift({
      cells: [
        {
          value: companyName,
          colSpan: sheet.columns.length,
          bold: true,
          fontSize: 14,
          textAlign: "center",
        },
      ],
    });
    excelExportRef.current.save(options);
  };

  const exportToPDF = () => {
    if (pdfExportRef.current) {
      pdfExportRef.current.save();
    }
  };

  useEffect(() => {}, [reportMenu]);

  const getRouteAbstractList = async () => {
    try {
      // Clear previous records before fetching new data
      setRouteAbstractList([]);

      // Format the dates to 'YYYY-MM-DD' (ISO 8601 format) before sending to the API
      const formattedFromDate = dayjs(fromDate).format("YYYY-MM-DD");
      const formattedToDate = dayjs(toDate).format("YYYY-MM-DD");

      const getRouteAbstractUrl = `${getEndPoint(
        API_END_POINT.REPORT
      )}/api/DailyReportsService/GetRouteAbstract/${formattedFromDate}/${formattedToDate}`;

      const response = await getDataWithToken<any>(getRouteAbstractUrl);

      const routeAbstract = response?.data?.result || [];
      console.log("routeAbstract", routeAbstract);
      setRouteAbstractList(routeAbstract);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const hasRecords = RouteAbstractList.length > 0;

  let grid = (
    <Grid
      data={RouteAbstractList}
      total={null}
      groupable={false}
      sortable={true}
      columnVirtualization={false}
      resizable={true}
    >
      <GridColumn field="rName" title="Route Name" columnMenu={null} />
      <GridColumn
        field="morningLiters"
        title="Morning Liters"
        columnMenu={null}
        cell={(props) => (
          <td style={{ textAlign: "right" }}>
            {props.dataItem.morningLiters?.toFixed(2)}
          </td>
        )}
        footerCell={(props) => {
          const totalMorningLiters = RouteAbstractList.reduce(
            (acc, item) => acc + (item.morningLiters || 0),
            0
          );
          return (
            <td colSpan={props.colSpan} style={{ textAlign: "right" }}>
              {totalMorningLiters.toFixed(2)}
            </td>
          );
        }}
      />
      <GridColumn
        field="eveningLiters"
        title="Evening Liters"
        columnMenu={null}
        cell={(props) => (
          <td style={{ textAlign: "right" }}>
            {props.dataItem.eveningLiters?.toFixed(2)}
          </td>
        )}
        footerCell={(props) => {
          const totalEveningLiters = RouteAbstractList.reduce(
            (acc, item) => acc + (item.eveningLiters || 0),
            0
          );
          return (
            <td colSpan={props.colSpan} style={{ textAlign: "right" }}>
              {totalEveningLiters.toFixed(2)}
            </td>
          );
        }}
      />
      <GridColumn
        field="totalLiters"
        title="Total Liters"
        cell={(props) => (
          <td style={{ textAlign: "right" }}>
            {props.dataItem.totalLiters?.toFixed(2)}
          </td>
        )}
        footerCell={(props) => {
          const totalLiters = RouteAbstractList.reduce(
            (acc, item) => acc + (item.totalLiters || 0),
            0
          );
          return (
            <td colSpan={props.colSpan} style={{ textAlign: "right" }}>
              {totalLiters.toFixed(2)}
            </td>
          );
        }}
      />
      <GridNoRecords>
        <div className="mt-2" style={{ height: "500px" }}>
          <center>
            <h5>No data found</h5>
          </center>
        </div>
      </GridNoRecords>
    </Grid>
  );

  return (
    <>
      <div
        className="m-lg-3 mt-lg-1 m-4 card p-3 border-0 m-3 sw-shadow radius-15"
        style={{ minHeight: "500px" }}
      >
        <div className="top-sec-align row">
          <center>
            <h4 className="text-normal title-web mb-0 report-title-color">
              Route Abstract
            </h4>
          </center>
        </div>
        <div className="fade_rule"></div>
        <div className="d-flex justify-content-between mt-4 pt-3 pb-3">
          <div className="d-flex">
            <div className="me-2">
              <label>From Date</label>
              <DatePicker
                selectsMultiple={true}
                openToDate={new Date()}
                className="form-control ms-auto search-right-align calendarBg"
                value={formatDate(fromDate, "dd/MM/yyyy")}
                placeholderText="From"
                dateFormat={"dd/MM/yyyy"}
                onChange={([date]: any) => {
                  setFromDate(date);
                }}
              />
            </div>
            <div className="me-2">
              <label>To Date</label>
              <DatePicker
                selectsMultiple={true}
                openToDate={new Date()}
                className="form-control ms-auto search-right-align calendarBg"
                value={formatDate(toDate, "dd/MM/yyyy")}
                placeholderText="To"
                dateFormat={"dd/MM/yyyy"}
                onChange={([date]: any) => {
                  setToDate(date);
                }}
              />
            </div>
            <div>
              <label></label>
              <button
                className="btn-primary btn search-btns mt-4"
                onClick={() => {
                  getRouteAbstractList();
                }}
                type="submit"
              >
                <svg width="17" height="17" viewBox="0 0 17.5 17.5">
                  <path
                    id="Path_magnify"
                    data-name="Path / magnify"
                    d="M9.5,3a6.5,6.5,0,0,1,4.94,10.73l.27.27h.79l5,5L19,20.5l-5-5v-.79l-.27-.27A6.5,6.5,0,1,1,9.5,3m0,2A4.5,4.5,0,1,0,14,9.5,4.481,4.481,0,0,0,9.5,5Z"
                    transform="translate(-3 -3)"
                    fill="#fff"
                  />
                </svg>
              </button>
            </div>
            <div className="d-flex justify-content-end">
              <button
                title="Export Excel"
                className="export-button ms-2"
                onClick={exportToExcel}
                disabled={!hasRecords}
                style={{ marginTop: "23px", marginBottom: "2px" }}
              >
                Export to Excel
              </button>
              <button
                title="Export PDF"
                className="export-button ms-2"
                onClick={exportToPDF}
                disabled={!hasRecords}
                style={{ marginTop: "23px", marginBottom: "2px" }}
              >
                Export to PDF
              </button>
            </div>
          </div>
        </div>
        <div className="row col=12">
          <LoadingIndicator isLoading={false} />
          <PDFExport
            fileName={"Route_Abstract.pdf"}
            ref={pdfExportRef}
            title="Route Abstract"
          >
            <div style={{ padding: "10px", backgroundColor: "#fff" }}>
              <div style={{ textAlign: "center", marginBottom: "5px" }}>
                <span style={{ fontWeight: "bold", fontSize: "1.5rem" }}>
                  {companyDetails?.[0]?.companyName || "Company Name"}
                </span>
              </div>
              <div style={{ textAlign: "center", marginBottom: "10px" }}>
                <span style={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                  Route Abstract Report
                  {fromDate && toDate && (
                    <>
                      {" - "}
                      {new Date(fromDate).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "numeric",
                        year: "numeric",
                      })}{" "}
                      to{" "}
                      {new Date(toDate).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "numeric",
                        year: "numeric",
                      })}
                    </>
                  )}
                </span>
              </div>
            </div>

            {grid}

            <ExcelExport
              fileName={"RouteAbstract_List"}
              data={RouteAbstractList}
              ref={excelExportRef}
            >
              <ExcelExportColumn field="rName" title="Route Name" />
              <ExcelExportColumn
                field="morningLiters"
                title="Morning Liters"
                footer={() => {
                  const totalMorningLiters = RouteAbstractList.reduce(
                    (acc, item) => acc + (item.morningLiters || 0),
                    0
                  );
                  return totalMorningLiters.toFixed(2);
                }}
              />
              <ExcelExportColumn
                field="eveningLiters"
                title="Evening Liters"
                footer={() => {
                  const totalEveningLiters = RouteAbstractList.reduce(
                    (acc, item) => acc + (item.eveningLiters || 0),
                    0
                  );
                  return totalEveningLiters.toFixed(2);
                }}
              />
              <ExcelExportColumn
                field="totalLiters"
                title="Total Liters"
                footer={() => {
                  const totalTotalLiters = RouteAbstractList.reduce(
                    (acc, item) => acc + (item.totalLiters || 0),
                    0
                  );
                  return totalTotalLiters.toFixed(2);
                }}
              />
            </ExcelExport>
          </PDFExport>
        </div>
      </div>
    </>
  );
};
